import { Fragment, useCallback, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Send, X } from 'lucide-react';

import { defineStepper } from '@ui/stepper.tsx';
import { Button } from '@ui/button.tsx';
import { Form } from '@ui/form.tsx';
import {
  useApiCountriesGetCollection,
  useApiAdminwaitingListsPost,
  useApiAdmintypologiesGetCollection
} from '@r/api/generated/client.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isTypologyConversion } from '@r/utils/workspace-utils.ts';
import type { WaitingListWaitingListWriteModel } from '@r/api/generated/client.schemas.ts';
import type { ApiCountry } from '@ui/country-dropdown.tsx';

import { WaitingListWaitingListWriteModelRole } from '@r/api/generated/client.schemas.ts';
import {
  buildWaitingListProposePayload,
  getWaitingListProposeCompanyStepFields
} from '@admin/components/company/waiting-list/waiting-list-propose-form-helpers.ts';
import {
  buildWaitingListProposeFormSchema,
  type WaitingListProposeFormValues
} from '@admin/components/company/waiting-list/waiting-list-propose-form-schema.ts';
import {
  WaitingListProposeCompanyFields,
  WaitingListProposeManagerFields
} from '@admin/components/company/waiting-list/waiting-list-propose-form-fields.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

const {
  StepperProvider,
  StepperNavigation,
  StepperStep,
  StepperTitle,
  StepperPanel,
  StepperControls
} = defineStepper(
  { id: 'step-company', title: 'company' },
  { id: 'step-manager', title: 'manager' }
);

export interface WaitingListProposeFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
}

export function WaitingListProposeForm({ onCancel, onSuccess }: WaitingListProposeFormProps) {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const workspaceConfig = useContext(WorkspaceConfigContext);
  const typologyConversionMode = isTypologyConversion(workspaceConfig);

  const typologiesQuery = useApiAdmintypologiesGetCollection(undefined, {
    query: { enabled: typologyConversionMode }
  });
  const typologies = (typologiesQuery.data?.data ?? []) as Array<{
    id: number;
    name: string;
  }>;

  const proposeSchema = useMemo(
    () => buildWaitingListProposeFormSchema(typologyConversionMode, t),
    [typologyConversionMode, t]
  );

  const stepCompanyFields = useMemo((): (keyof WaitingListProposeFormValues)[] => {
    return getWaitingListProposeCompanyStepFields(typologyConversionMode);
  }, [typologyConversionMode]);

  const form = useForm<WaitingListProposeFormValues>({
    resolver: zodResolver(proposeSchema),
    defaultValues: {
      companyName: '',
      companyUniqueCode: '',
      typology: '',
      tva: '',
      siren: '',
      addressLine1: '',
      addressLine2: '',
      zipcode: '',
      city: '',
      countryAlpha3: '',
      email: '',
      firstname: '',
      lastname: '',
      role: '',
      comment: '',
      pointCoeff: undefined
    },
    mode: 'onChange'
  });

  const { data: countriesResponse, isLoading: isCountriesLoading } = useApiCountriesGetCollection();
  const countries = (countriesResponse?.data as ApiCountry[] | undefined) ?? [];

  const postMutation = useApiAdminwaitingListsPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.waiting.list.create.success'));
        queryClient.invalidateQueries({ queryKey: ['waiting_list_collection'] });
        onSuccess?.();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.waiting.list.create.error'));
      }
    }
  });

  const submit = useCallback(async () => {
    const valid = await form.trigger();
    if (!valid) return;
    const values = form.getValues();
    const payload: WaitingListWaitingListWriteModel = buildWaitingListProposePayload(
      values,
      typologyConversionMode
    );
    postMutation.mutate({ data: payload });
  }, [form, postMutation, typologyConversionMode]);

  const handleStepNext = useCallback(
    async (methods: { current: { id: string }; next: () => void }) => {
      if (methods.current.id === 'step-company') {
        const ok = await form.trigger(stepCompanyFields);
        if (!ok) return;
        methods.next();
        return;
      }
      await submit();
    },
    [form, stepCompanyFields, submit]
  );

  return (
    <Form {...form}>
      <StepperProvider variant="horizontal" labelOrientation="vertical" className="w-full">
        {({ methods }) => {
          const currentIndex = methods.all.findIndex((s) => s.id === methods.current.id);
          const isFirst = currentIndex === 0;
          const isLast = currentIndex === methods.all.length - 1;

          return (
            <Fragment>
              <StepperNavigation className="mb-8">
                {methods.all.map((step) => (
                  <StepperStep key={step.id} of={step.id}>
                    <StepperTitle>
                      {step.id === 'step-company'
                        ? t('admin.waiting.list.step.company')
                        : t('admin.waiting.list.step.manager')}
                    </StepperTitle>
                  </StepperStep>
                ))}
              </StepperNavigation>

              <div className="min-h-[280px] py-2">
                {methods.switch({
                  'step-company': () => (
                    <StepperPanel>
                      <WaitingListProposeCompanyFields
                        form={form}
                        countries={countries}
                        isCountriesLoading={isCountriesLoading}
                        typologyConversionMode={typologyConversionMode}
                        typologies={typologies}
                        typologiesLoading={typologiesQuery.isLoading}
                        t={t}
                      />
                    </StepperPanel>
                  ),
                  'step-manager': () => (
                    <StepperPanel>
                      <WaitingListProposeManagerFields
                        form={form}
                        roleOptions={[
                          {
                            value: WaitingListWaitingListWriteModelRole.ROLE_MEMBER,
                            label: t('role.member')
                          },
                          {
                            value: WaitingListWaitingListWriteModelRole.ROLE_MANAGER,
                            label: t('role.manager')
                          }
                        ]}
                        t={t}
                      />
                    </StepperPanel>
                  )
                })}
              </div>

              <StepperControls className="flex flex-row flex-wrap gap-4 justify-between pt-6 w-full">
                {isFirst ? (
                  <Button
                    type="button"
                    variant="outline"
                    rounded="full"
                    onClick={onCancel}
                    className="flex gap-2 items-center"
                    disabled={postMutation.isPending}
                  >
                    <X className="w-4 h-4" />
                    {t('common.cancel')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    rounded="full"
                    onClick={() => methods.prev()}
                    className="flex gap-2 items-center"
                    disabled={postMutation.isPending}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t('common.previous')}
                  </Button>
                )}

                <Button
                  type="button"
                  rounded="full"
                  onClick={() => handleStepNext(methods)}
                  className="flex gap-2 items-center"
                  disabled={postMutation.isPending}
                >
                  {isLast ? (
                    postMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('common.sending')}
                      </>
                    ) : (
                      <>
                        {t('admin.waiting.list.cta.button')}
                        <Send className="w-4 h-4" />
                      </>
                    )
                  ) : (
                    <>
                      {t('common.next')}
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </StepperControls>
            </Fragment>
          );
        }}
      </StepperProvider>
    </Form>
  );
}
